import { useState, useCallback, useRef } from 'react';
import { notificationService } from '@/services';
import { useHouseholdStore } from '@/stores';
import type {
  Notification,
  NotificationFilter,
  NotificationCursor,
} from '@/types';

/**
 * Activity list hook with keyset pagination and All/Unread filtering.
 *
 * Scoped to the ACTIVE household. This used to read "RLS scopes all queries
 * to the current user automatically — no householdId parameter needed", which
 * was the bug: RLS scopes to the user, and the user spans every household
 * they belong to, so a member of two households saw both feeds interleaved
 * (YAR-358).
 */
export function useNotifications() {
  const [items, setItems] = useState<Notification[]>([]);
  const [cursor, setCursor] = useState<NotificationCursor>(null);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const householdId = useHouseholdStore((s) => s.householdId);

  // Refs to avoid stale closures in load callback
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const filterRef = useRef(filter);
  filterRef.current = filter;
  // Read through a ref for the same reason as cursor/filter: `load` is
  // deliberately identity-stable and Activity calls it from a mount effect.
  const householdIdRef = useRef(householdId);
  householdIdRef.current = householdId;

  // ── Concurrency control (YAR-388) ────────────────────────────────────
  //
  // Every page of this feed used to be written by whichever request happened
  // to resolve last, with nothing tying a response back to the request that
  // asked for it. That is how the unread count doubled on a filter switch:
  //
  //   1. `changeFilter` empties the list and optimistically sets hasMore=true,
  //      so Activity's infinite-scroll sentinel re-mounts at the top of a now
  //      empty list.
  //   2. Activity's `[filter]` effect calls `load(true)` — a RESET read of
  //      page 1.
  //   3. The IntersectionObserver, whose `!loading` guard reads a `loading`
  //      captured in the previous render, sees the sentinel and calls `load()`
  //      — an APPEND read. `cursor` is still null, so it re-reads *the same*
  //      page 1.
  //   4. If the reset lands first, the append concatenates that identical page
  //      onto it: 4 unread rows render as 8. If the append lands first, the
  //      reset overwrites it and nothing looks wrong — hence "intermittent".
  //
  // Two invariants close it, and both are needed:
  //
  //   * `seq` — a response may only be written if no newer request has started
  //     since. Covers the reverse ordering of the race above, a filter or
  //     household switch overtaking an in-flight read, and a stale read
  //     repopulating the list after the household was cleared.
  //   * `inFlight` — an append may never start while any request is in flight,
  //     because the cursor it would read is the one the in-flight request is
  //     about to move. This also stops two rapid sentinel hits appending the
  //     same page twice.
  //
  // `loading` follows `inFlight` rather than a single request, so it stays
  // true until the feed is actually settled.
  const seqRef = useRef(0);
  const inFlightRef = useRef(0);

  /** Load first page (reset=true) or next page (reset=false).
   *  Uses refs to read current cursor/filter — stable callback identity. */
  const load = useCallback(
    async (reset = false) => {
      const currentHouseholdId = householdIdRef.current;
      // No active household yet (first paint before resolution, or a stale pin
      // that was cleared). Showing nothing is correct — the alternative is an
      // unscoped query, which is the leak this hook exists to prevent.
      if (!currentHouseholdId) {
        if (reset) {
          // Retire anything in flight for the household we just left, so its
          // response cannot repopulate the list we are clearing.
          seqRef.current += 1;
          setItems([]);
          setCursor(null);
          setHasMore(false);
        }
        return;
      }

      if (!reset && inFlightRef.current > 0) return;

      const seq = ++seqRef.current;
      inFlightRef.current += 1;
      setLoading(true);
      try {
        const currentCursor = reset ? null : cursorRef.current;
        const { data, nextCursor } =
          await notificationService.listNotifications(
            currentHouseholdId,
            filterRef.current,
            currentCursor,
          );
        // Superseded while we were away — a newer request owns the list now.
        if (seq !== seqRef.current) return;
        setItems((prev) => (reset ? data : [...prev, ...data]));
        setCursor(nextCursor);
        setHasMore(nextCursor !== null);
      } finally {
        inFlightRef.current -= 1;
        if (inFlightRef.current === 0) setLoading(false);
      }
    },
    [], // stable — reads from refs
  );

  /** Switch between All / Unread filter chips. Resets the list. */
  const changeFilter = useCallback((newFilter: NotificationFilter) => {
    // FilterChipGroup fires onChange for the chip that is already selected, and
    // re-tapping it used to empty the list without changing `filter` — so the
    // `[filter]` effect that refills it never ran, and the screen was left
    // relying on the infinite-scroll sentinel noticing and re-reading page 1.
    // Nothing about this is a refresh gesture; make it a no-op.
    if (filterRef.current === newFilter) return;
    setFilter(newFilter);
    setCursor(null);
    setItems([]);
    setHasMore(true);
  }, []);

  /** Tap an item — mark as read optimistically. Caller navigates. */
  const tapItem = useCallback(async (id: string) => {
    await notificationService.markAsRead(id);
    setItems((prev) =>
      prev.map((n) =>
        n.id === id
          ? { ...n, read_at: n.read_at ?? new Date().toISOString() }
          : n,
      ),
    );
  }, []);

  /** Overflow menu — "Mark all read". Scoped to the active household. */
  const markAllAsRead = useCallback(async () => {
    const currentHouseholdId = householdIdRef.current;
    if (!currentHouseholdId) return;

    await notificationService.markAllAsRead(currentHouseholdId);
    setItems((prev) =>
      prev.map((n) => ({
        ...n,
        read_at: n.read_at ?? new Date().toISOString(),
      })),
    );
  }, []);

  /** Dismiss a single notification. */
  const dismiss = useCallback(async (id: string) => {
    await notificationService.dismissNotification(id);
    setItems((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return {
    items,
    filter,
    hasMore,
    loading,
    load,
    changeFilter,
    tapItem,
    markAllAsRead,
    dismiss,
    /**
     * Exposed so the screen can re-run `load` when the household changes.
     * `load` is identity-stable by design, so it cannot carry that signal
     * itself, and a household switch must not leave the previous feed on
     * screen.
     */
    householdId,
  };
}
